import { debug } from '../log';
import { globalVar } from '../universal';
import { AppSetting } from 'dependencies/appsettings';

/**
 * For browsers that does not support navigator.languages, ensure empty array is resolved
 */
const userLocales = () => {
  const nav = globalVar('navigator');
  return (nav.languages && nav.languages.length) ? nav.languages : [];
};

/**
 * Languages that does not have a primary written language e.g Norwegian, where
 * "no" is not valid for INTL, but only the specific language nb-NO or nb-NN
 */
const localeFallbacks: any = {
  no: 'nb-NO',
  nb: 'nb-NO',
  nn: 'nn-NO'
};

/**
 * Retrive the users browser preferences for content display language as an array.
 * Future: This should not be necessary once we've added language and locale to the user profile
 */
function getAcceptedLanguages(): string[] {
  let matches: string[] = [];
  const nav = globalVar('navigator');
  if (nav) {
    matches = (nav.languages && nav.languages.length ? [nav.languages[0]] : undefined)
                || [ nav.language || nav.userLanguage || nav.browserLanguage || 'en' ];
    matches = matches.filter(x => !!x);
  }

  // now, matches may contain either `language` (en) or `language-country-<optional>` (nb-NO-(...))
  // to be able to load polyfills, ensure the language is at position 0
  const lang = getLanguageFromIetfLanguageTag(matches[0]);
  if ( lang !== matches[0] ) {
    matches.unshift(lang);
  }
  return matches;
}

/**
 * Resolve HTTP header `accept-language` from app settings global
 */
function getHttpHeaderLanguages() : string[] {
  const allLanguages = AppSetting.AcceptedLanguage;

  if (!allLanguages) {
    return [];
  }

  const result = allLanguages.toString().split(',').map( (s: string) => s.split(';')[0]);
  const primary = getLanguageFromIetfLanguageTag(result[0]);
  if (primary !== result[0]) {
    result.unshift(primary);
  }
  return result;
}

/**
 * Returns the users preferred locale, with fallback to http request language used as locale (IE, Edge)
 * Future: This should not be necessary in auth scenarios once we've added language and locale to the user profile.
 * Once read from userprofile should always be avaliable from localStorage
 */
export function inferPrimaryIntlLocale(){

  const httpRequestLanguages = AppSetting.AcceptedLanguage || 'en';
  const languageCodesFromRequest = httpRequestLanguages.toString().split(',').map( (s: string) => s.split(';')[0]);
  const locales = userLocales().length ? userLocales() : languageCodesFromRequest;

  const supported = locales.map((locale: string) => {

    const isSupported = (Intl.DateTimeFormat.supportedLocalesOf(locale).length &&
                                      Intl.NumberFormat.supportedLocalesOf(locale).length);

    const isSupportedWithFallback = (Intl.DateTimeFormat.supportedLocalesOf(localeFallbacks[locale]).length &&
                                      Intl.NumberFormat.supportedLocalesOf(localeFallbacks[locale]).length);

    if (isSupported){
      return locale;
    }

    if (isSupportedWithFallback){
      return localeFallbacks[locale];
    }

    return 'no';

  }).filter( (defined: any) => defined );

  return supported[0];
}

function getLanguageFromIetfLanguageTag(locale: string) {
  if (locale && locale.indexOf('-') > -1) {
    const parts = locale.split('-');
    return parts[0];
  }
  return locale;
}

/**
 * Return first locale match in HTTP header "Accept-Language"" value
 * Only used for messages in non-auth scenarios
 */
export function inferContentLanguage() {
  return getHttpHeaderLanguages()[0] || getAcceptedLanguages()[0];
}

/**
 * Loads the given react-intl bullshit locale on demand from a code-split bundle
 * to have date and number formatting in the UI that reflects the users current language locale
 *
 * The react-intl locale data files are provide per language, e.g.
 * en.js, but they contain data for all locales in that language, e.g. en-US, en-GB, etc.
 *
 * Future: aim to remove react-intl - probably overkill
 *
 * @export
 * @param {string} locale
 */
export async function loadLocale() {

  async function loadLocaleData() {
    const currentLang = inferContentLanguage();
    debug('loading intl settings for ' + currentLang);
    debug('Locale loaded');
  }

  try {
    return loadLocaleData();
  } catch (e) {
    return debug(`Error loading locale: ${e}`);
  }
}

// getCanonicalLocales will validate and return locales. This fixes 'no' locale and replaces with 'nb'
export function getCanonicalLocale(locale: string) {
  const intl = globalVar('Intl');
  return locale && intl?.getCanonicalLocales(locale)[0] || 'en';
}
